<template>
  <InfuseTable
    :columns="columns"
    :rows="filteredRows"
    :active="active"
    :loading="loading"
    :has-more-items="hasMoreItems"
    :search="search"
    :actions="rowActions"
    :filters="filters"
    :filter-configs="filterConfigs"
    :no-data-title="t('iam.access-requests.no-data.title')"
    :no-data-description="t('iam.access-requests.no-data.description')"
    @search="(query: string) => emit('search', query)"
    @load="emit('load')"
    @action="onAction"
    @click-row="(row) => emit('clickRow', row as AccessRequest)"
    @add:filter="onAddFilter"
    @remove:filter="onRemoveFilter"
  >
    <template #status="{ row }">
      <AccessRequestStatusBadge :status="(row as AccessRequest).status" />
    </template>

    <template #requestedAt="{ data }">
      {{ data ? new Date(data).toLocaleString() : '' }}
    </template>
  </InfuseTable>
</template>

<script setup lang="ts">
import { FilterExpression } from '@infuse/api/common';
import { AccessRequest, AccessRequestStatus } from '@infuse/api/iam/access-request';
import { InfuseTable } from '@infuse/ui/components';
import type { Filter, FilterConfiguration, FilterOption, UiRowAction, UiTableColumn } from '@infuse/ui/domain';
import { FilterConfigurationType } from '@infuse/ui/domain';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import AccessRequestStatusBadge from './AccessRequestStatusBadge.vue';

const { t } = useI18n();

interface Props {
  rows: AccessRequest[];
  loading?: boolean;
  hasMoreItems?: boolean;
  searchQuery?: string;
  searchEnabled?: boolean;
  canApprove?: boolean;
  canDeny?: boolean;
  showRowActions?: boolean;
  active?: (row: AccessRequest, id?: number) => boolean;
  /** Initial status; defaults to PENDING. */
  initialStatus?: AccessRequestStatus;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  hasMoreItems: true,
  searchQuery: '',
  searchEnabled: true,
  canApprove: true,
  canDeny: true,
  showRowActions: true,
  active: () => false,
  initialStatus: AccessRequestStatus.PENDING
});

interface Emits {
  (e: 'search', query: string): void;
  (e: 'load'): void;
  (e: 'approve', row: AccessRequest): void;
  (e: 'deny', row: AccessRequest): void;
  (e: 'clickRow', row: AccessRequest): void;
}

const emit = defineEmits<Emits>();

const buildStatusOption = (status: AccessRequestStatus): FilterOption => ({
  name: status,
  label: () => t(`iam.access-requests.status.${status.toLowerCase()}`),
  toUmbrellaExpression: () => new FilterExpression().equals('status', status, true)
});

const statusConfig: FilterConfiguration = {
  name: 'status',
  label: () => t('iam.access-requests.column.status'),
  type: FilterConfigurationType.SELECT,
  singleton: true,
  options: [
    buildStatusOption(AccessRequestStatus.PENDING),
    buildStatusOption(AccessRequestStatus.APPROVED),
    buildStatusOption(AccessRequestStatus.DENIED),
    buildStatusOption(AccessRequestStatus.CANCELLED)
  ]
};

const filterConfigs: FilterConfiguration[] = [statusConfig];

const filters = ref<Filter[]>([
  {
    config: statusConfig,
    selectedOption: statusConfig.options.find((o) => o.name === props.initialStatus) ?? statusConfig.options[0],
    negated: false
  }
]);

const filteredRows = computed(() => {
  const statusFilter = filters.value.find((f) => f.config.name === 'status');
  if (!statusFilter) {
    return props.rows;
  }
  const wanted = statusFilter.selectedOption.name as AccessRequestStatus;
  return statusFilter.negated
    ? props.rows.filter((r) => r.status !== wanted)
    : props.rows.filter((r) => r.status === wanted);
});

function onAddFilter(filter: Filter): void {
  // singleton: replace any existing filter on the same config
  const idx = filters.value.findIndex((f) => f.config.name === filter.config.name);
  if (idx >= 0) {
    filters.value.splice(idx, 1, filter);
  } else {
    filters.value.push(filter);
  }
}

function onRemoveFilter(filter: Filter): void {
  filters.value = filters.value.filter(
    (f) => !(f.config.name === filter.config.name && f.selectedOption.name === filter.selectedOption.name)
  );
}

const search = computed(() =>
  !props.searchEnabled
    ? undefined
    : {
        placeholder: t('iam.access-requests.search-placeholder'),
        query: props.searchQuery
      }
);

const columns: UiTableColumn[] = [
  {
    name: 'requester',
    label: () => t('iam.access-requests.column.requester'),
    field: 'requesterName',
    bold: true
  },
  {
    name: 'email',
    label: () => t('iam.access-requests.column.email'),
    field: 'requesterEmail'
  },
  {
    name: 'status',
    label: () => t('iam.access-requests.column.status'),
    field: 'status'
  },
  {
    name: 'requestedAt',
    label: () => t('iam.access-requests.column.requested-at'),
    field: 'requestedAt'
  }
];

const rowActions = computed<UiRowAction[]>(() => {
  const actions: UiRowAction[] = [];
  if (!props.showRowActions) {
    return actions;
  }
  if (props.canApprove) {
    actions.push({
      event: 'approve',
      icon: ['fas', 'check'],
      label: () => t('iam.access-requests.action.approve'),
      enabled: (row: AccessRequest) => row.status === AccessRequestStatus.PENDING,
      class:
        'cursor-pointer rounded-full p-2 text-success-500 transition-colors hover:bg-success-50 hover:text-success-600'
    });
  }
  if (props.canDeny) {
    actions.push({
      event: 'deny',
      icon: ['fas', 'xmark'],
      label: () => t('iam.access-requests.action.deny'),
      enabled: (row: AccessRequest) => row.status === AccessRequestStatus.PENDING,
      class:
        'cursor-pointer rounded-full p-2 text-error-500 transition-colors hover:bg-error-50 hover:text-error-600'
    });
  }
  return actions;
});

function onAction(event: string, row: AccessRequest): void {
  if (event === 'approve') emit('approve', row);
  if (event === 'deny') emit('deny', row);
}
</script>
